import React, { useEffect, useMemo, useState } from 'react';
import { suppliersAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  BarChart3, Filter, ChevronDown, ChevronRight, Truck, ShieldCheck, Building2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { DeliveryDrilldown, QualityDrilldown } from '@/components/SupplierPerformanceDrilldown';

const isoDaysAgo = (days) => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString().slice(0, 10);
};

const pctBadge = (pct) => {
  if (pct === null || pct === undefined) return <span className="text-slate-400">—</span>;
  const cls = pct >= 90 ? 'bg-emerald-100 text-emerald-700' : pct >= 75 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';
  return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{pct}%</span>;
};

const SupplierRow = ({ s }) => {
  const [open, setOpen] = useState(null); // 'delivery' | 'quality' | null
  return (
    <>
      <tr className="border-b hover:bg-slate-50" data-testid={`report-row-${s.supplier_id}`}>
        <td className="py-2 px-3">
          <div className="font-medium text-slate-800">{s.supplier_name}</div>
          <div className="text-[10px] text-slate-400 font-mono">{s.supplier_code || s.supplier_external_id || ''}</div>
        </td>
        <td className="py-2 px-3 text-center">
          <button
            type="button"
            onClick={() => setOpen(open === 'delivery' ? null : 'delivery')}
            className="inline-flex items-center gap-1 hover:underline"
            data-testid={`report-delivery-${s.supplier_id}`}
          >
            {open === 'delivery' ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {pctBadge(s.delivery.on_time_pct)}
          </button>
        </td>
        <td className="py-2 px-3 text-center text-xs text-slate-600">
          {s.delivery.on_time}/{s.delivery.total_evaluated}
        </td>
        <td className="py-2 px-3 text-center">
          <button
            type="button"
            onClick={() => setOpen(open === 'quality' ? null : 'quality')}
            className="inline-flex items-center gap-1 hover:underline"
            data-testid={`report-quality-${s.supplier_id}`}
          >
            {open === 'quality' ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {pctBadge(s.quality.quality_pct)}
          </button>
        </td>
        <td className="py-2 px-3 text-center text-xs text-slate-600">{s.quality.total_inspected_qty}</td>
        <td className="py-2 px-3 text-center text-xs">
          {s.quality.mrb_qty > 0
            ? <span className="text-amber-700 font-medium">{s.quality.mrb_qty}</span>
            : <span className="text-slate-400">0</span>}
        </td>
      </tr>
      {open === 'delivery' && (
        <tr className="bg-slate-50/60"><td colSpan={6} className="px-3 py-2">
          <div className="text-[11px] font-medium text-slate-600 mb-1 flex items-center gap-1"><Truck className="w-3 h-3 text-blue-600" /> Delivery backup — {s.supplier_name}</div>
          <DeliveryDrilldown rows={s.delivery.rows} />
        </td></tr>
      )}
      {open === 'quality' && (
        <tr className="bg-slate-50/60"><td colSpan={6} className="px-3 py-2">
          <div className="text-[11px] font-medium text-slate-600 mb-1 flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-emerald-600" /> Quality backup — {s.supplier_name}</div>
          <QualityDrilldown rows={s.quality.rows} />
        </td></tr>
      )}
    </>
  );
};

export default function SupplierPerformanceReportPage() {
  const [suppliers, setSuppliers] = useState([]);
  const [selected, setSelected] = useState([]); // supplier ids
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState(isoDaysAgo(365));
  const [dateTo, setDateTo] = useState(new Date().toISOString().slice(0, 10));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    suppliersAPI.list({ limit: 500 })
      .then((res) => setSuppliers(Array.isArray(res.data) ? res.data : (res.data.suppliers || [])))
      .catch(() => toast.error('Failed to load suppliers'));
  }, []);

  const filteredSuppliers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return suppliers;
    return suppliers.filter((s) =>
      (s.supplier_name || '').toLowerCase().includes(q) ||
      (s.supplier_code || '').toLowerCase().includes(q) ||
      (s.supplier_id || '').toLowerCase().includes(q));
  }, [suppliers, search]);

  const toggle = (id) => {
    setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const generate = async () => {
    if (dateFrom > dateTo) { toast.error('From date must be before To date'); return; }
    setLoading(true);
    try {
      const res = await suppliersAPI.performanceReport({
        supplier_ids: selected,
        date_from: dateFrom,
        date_to: dateTo,
      });
      setReport(res.data);
      if (res.data.total === 0) toast.info('No suppliers matched');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate report');
    } finally {
      setLoading(false);
    }
  };

  const selectedNames = suppliers.filter((s) => selected.includes(s.id));

  return (
    <div className="space-y-4" data-testid="supplier-performance-report-page">
      <div className="flex items-center gap-2">
        <BarChart3 className="w-5 h-5 text-indigo-600" />
        <h1 className="text-xl font-bold tracking-tight text-[#0F172A]">Supplier Performance Report</h1>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2"><Filter className="w-4 h-4" /> Filters</CardTitle>
          <CardDescription className="text-xs">Select one or more suppliers and a date range, then generate the report. Leave suppliers empty to include all.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div className="md:col-span-2">
            <Label className="text-xs">Suppliers</Label>
            <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-between h-9 text-xs font-normal" data-testid="supplier-picker-trigger">
                  <span className="flex items-center gap-1 truncate">
                    <Building2 className="w-3.5 h-3.5" />
                    {selected.length === 0 ? 'All suppliers' : `${selected.length} selected`}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 opacity-60" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[320px] p-0" align="start">
                <div className="p-2 border-b">
                  <Input
                    placeholder="Search suppliers..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="h-8 text-xs"
                    data-testid="supplier-picker-search"
                  />
                  <div className="flex justify-between mt-2">
                    <button type="button" className="text-[11px] text-indigo-600 hover:underline" onClick={() => setSelected(filteredSuppliers.map((s) => s.id))}>Select all shown</button>
                    <button type="button" className="text-[11px] text-slate-500 hover:underline" onClick={() => setSelected([])}>Clear</button>
                  </div>
                </div>
                <ScrollArea className="h-64">
                  <div className="p-1">
                    {filteredSuppliers.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-100 cursor-pointer text-xs" data-testid={`supplier-option-${s.id}`}>
                        <Checkbox checked={selected.includes(s.id)} onCheckedChange={() => toggle(s.id)} />
                        <span className="truncate">{s.supplier_name}</span>
                      </label>
                    ))}
                    {filteredSuppliers.length === 0 && <p className="text-xs text-slate-400 p-3">No suppliers found.</p>}
                  </div>
                </ScrollArea>
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-xs">From</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="h-9 text-xs" data-testid="report-date-from" />
          </div>
          <div>
            <Label className="text-xs">To</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="h-9 text-xs" data-testid="report-date-to" />
          </div>
          <div className="md:col-span-4 flex items-center justify-between">
            <div className="flex flex-wrap gap-1">
              {selectedNames.slice(0, 6).map((s) => (
                <Badge key={s.id} variant="outline" className="text-[10px] gap-1">
                  {s.supplier_name}
                  <X className="w-3 h-3 cursor-pointer" onClick={() => toggle(s.id)} />
                </Badge>
              ))}
              {selectedNames.length > 6 && <Badge variant="outline" className="text-[10px]">+{selectedNames.length - 6} more</Badge>}
            </div>
            <Button onClick={generate} disabled={loading} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="generate-report-btn">
              {loading ? 'Generating…' : 'Generate Report'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {report && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Results ({report.total})</CardTitle>
            <CardDescription className="text-xs">Period {report.date_from} → {report.date_to}. Click an On-Time or Quality figure to see the backup rows; click a PO number to open it.</CardDescription>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="report-results-table">
              <thead className="text-slate-500 border-b text-xs">
                <tr>
                  <th className="text-left font-medium py-2 px-3">Supplier</th>
                  <th className="text-center font-medium py-2 px-3">On-Time %</th>
                  <th className="text-center font-medium py-2 px-3">On-Time / Total</th>
                  <th className="text-center font-medium py-2 px-3">Quality %</th>
                  <th className="text-center font-medium py-2 px-3">Inspected Qty</th>
                  <th className="text-center font-medium py-2 px-3">MRB Qty</th>
                </tr>
              </thead>
              <tbody>
                {report.suppliers.map((s) => <SupplierRow key={s.supplier_id} s={s} />)}
              </tbody>
            </table>
            {report.suppliers.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">No data for the selected filters.</p>}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
